import { StateCreator } from "zustand";

import { reorderColumns, withAnchoredColumnFirst } from "ds/components/Table/Head/helpers";

import { type TableColumnsState } from "../types";

const makeTableColumnsSlice =
  <T extends TableColumnsState>(
    config: TableColumnsState["columnsState"]
  ): StateCreator<T, [["zustand/immer", never]], [["zustand/persist", never]], TableColumnsState> =>
  (set) => {
    const anchoredColumn = Object.keys(config).find((columnId) => config[columnId].anchored);
    const initialColumnsState = config;
    const initialColumnsSizes = Object.fromEntries(
      Object.entries(config).map(([key, value]) => [key, value.defaultWidth])
    );
    const initialColumnsOrder = withAnchoredColumnFirst(
      { visible: Object.keys(config), hidden: [] },
      anchoredColumn
    );

    const initialColumnPinned = true;

    return {
      columnsState: initialColumnsState,
      anchoredColumn,
      columnsSizes: initialColumnsSizes,
      setColumnSize: (columnId, size) => {
        set((state) => {
          state.columnsSizes[columnId] = size;
        });
      },
      columnsOrder: initialColumnsOrder,
      reorderVisibleColumns: (columnId, direction) => {
        set((state) => {
          state.columnsOrder = withAnchoredColumnFirst(
            {
              visible: reorderColumns(state.columnsOrder.visible, columnId, direction),
              hidden: [...state.columnsOrder.hidden],
            },
            anchoredColumn
          );
        });
      },
      hideColumn: (columnId) => {
        set((state) => {
          if (columnId === anchoredColumn) {
            return;
          }

          state.columnsOrder = {
            visible: state.columnsOrder.visible.filter((id) => id !== columnId),
            hidden: state.columnsOrder.hidden.includes(columnId)
              ? state.columnsOrder.hidden
              : [...state.columnsOrder.hidden, columnId],
          };
        });
      },
      setColumnsOrder: ({ visible, hidden }) => {
        set((state) => {
          state.columnsOrder = withAnchoredColumnFirst({ visible, hidden }, anchoredColumn);
        });
      },
      resetColumnsConfig: () => {
        set((state) => {
          state.columnsState = initialColumnsState;
          state.columnsSizes = initialColumnsSizes;
          state.columnsOrder = initialColumnsOrder;
          state.isFirstColumnPinned = initialColumnPinned;
        });
      },
      isFirstColumnPinned: initialColumnPinned,
      pinColumn: (columnId) => {
        set((state) => {
          const currentIndex = state.columnsOrder.visible.indexOf(columnId);

          if (currentIndex === 0) {
            state.isFirstColumnPinned = !state.isFirstColumnPinned;
          } else if (currentIndex > 0) {
            const movedToFront = [...state.columnsOrder.visible];
            movedToFront.splice(currentIndex, 1);
            movedToFront.unshift(columnId);

            const order = withAnchoredColumnFirst(
              { visible: movedToFront, hidden: [...state.columnsOrder.hidden] },
              anchoredColumn
            );
            const pinTookFirstPosition = order.visible[0] === columnId;

            if (!pinTookFirstPosition) {
              return;
            }

            state.columnsOrder = order;
            state.isFirstColumnPinned = true;
          }
        });
      },
    };
  };

export default makeTableColumnsSlice;
